/**
 * Outbound Call Report — the Analytics view.
 *
 * The mirror of the inbound view, asking the outbound question: of the attempts
 * placed, how many a human picked up, what the failures were, and what the
 * dialler dropped.
 *
 * ABANDONED IS SHOWN APART FROM EVERY OTHER FAILURE, deliberately. On outbound
 * it means the DIALLER dropped a customer who had already answered — the number
 * regulators cap at 3% — and folding it into "unanswered" would bury the one
 * figure with a legal ceiling on it among the ordinary busy signals.
 *
 * Averages are weighted by call volume, not by interval — see the note in the
 * inbound view.
 */
import { useMemo, type JSX } from 'react'
import { Chart } from 'primereact/chart'
import {
  CalendarClock, Layers, PhoneForwarded, PhoneOff, Timer, TrendingUp, Users,
} from 'lucide-react'

import {
  AnalyticsCanvas,
  BarList,
  CardEmpty,
  CardGrid,
  ChartCard,
  HeroBand,
  KpiRow,
  Meter,
  SectionLabel,
  StatTile,
} from '../analytics/AnalyticsKit.js'
import {
  VIZ,
  barDataset,
  barOptions,
  formatCount,
  formatPct,
  formatSeconds,
  lineDataset,
  lineOptions,
  ratePct,
} from '../analytics/viz.js'
import {
  BAND_LABELS,
  bandTotals,
  bucketLabel,
  bucketSeries,
  peakOf,
  sumBoardBy,
  sumCol,
  weightedAvgCol,
} from './analytics-helpers.js'

type Row = Record<string, unknown>

/** The regulatory ceiling the predictive pacer targets (Ofcom, §2.2/B8). */
const ABANDON_CAP_PCT = 3

export default function OutboundCallAnalytics({ rows }: { rows: readonly Row[] }): JSX.Element {
  const totals = useMemo(
    () => ({
      offered: sumCol(rows, 'callsOffered'),
      answered: sumCol(rows, 'callsAnswered'),
      unanswered: sumCol(rows, 'callsUnanswered'),
      busy: sumCol(rows, 'busy'),
      noAnswer: sumCol(rows, 'noAnswer'),
      rejected: sumCol(rows, 'rejected'),
      abandoned: sumCol(rows, 'abandoned'),
      destinations: sumCol(rows, 'uniqueDestinations'),
      talk: sumCol(rows, 'totalTalkSec'),
      handle: sumCol(rows, 'totalHandleSec'),
      ring: sumCol(rows, 'totalRingSec'),
      cost: sumCol(rows, 'totalCost'),
    }),
    [rows],
  )

  const avgHandle = useMemo(() => weightedAvgCol(rows, 'avgHandleSec', 'callsAnswered'), [rows])
  const avgTalk = useMemo(() => weightedAvgCol(rows, 'avgTalkSec', 'callsAnswered'), [rows])
  const avgRing = useMemo(() => weightedAvgCol(rows, 'avgRingSec', 'callsOffered'), [rows])

  const offered = useMemo(() => bucketSeries(rows, 'bucketStart', 'callsOffered'), [rows])
  const answered = useMemo(() => bucketSeries(rows, 'bucketStart', 'callsAnswered'), [rows])
  const abandoned = useMemo(() => bucketSeries(rows, 'bucketStart', 'abandoned'), [rows])
  const busiest = useMemo(() => peakOf(offered), [offered])

  const volumeData = useMemo(
    () => ({
      labels: offered.labels,
      datasets: [
        lineDataset('Attempted', offered.values, VIZ.series[0], { fill: true }),
        lineDataset('Connected', answered.values, VIZ.series[1]),
      ],
    }),
    [offered, answered],
  )

  // Connect rate and abandon rate share an axis on purpose: pushing the pacer
  // for a higher connect rate is what drives abandons up, and the trade-off is
  // only visible when the two are drawn together.
  const connectRateSeries = useMemo(
    () => offered.labels.map((_, i) => ratePct(answered.values[i] ?? 0, offered.values[i] ?? 0) ?? 0),
    [offered, answered],
  )
  const abandonRateSeries = useMemo(
    () => offered.labels.map((_, i) => ratePct(abandoned.values[i] ?? 0, offered.values[i] ?? 0) ?? 0),
    [offered, abandoned],
  )

  const rateData = useMemo(
    () => ({
      labels: offered.labels,
      datasets: [
        lineDataset('Connect rate %', connectRateSeries, VIZ.series[1]),
        lineDataset('Abandon rate %', abandonRateSeries, VIZ.status.critical, { fill: true }),
      ],
    }),
    [offered, connectRateSeries, abandonRateSeries],
  )

  const ringBands = useMemo(() => bandTotals(rows, 'answered'), [rows])
  const bandData = useMemo(
    () => ({
      labels: [...BAND_LABELS],
      datasets: [barDataset('Connected', ringBands, VIZ.series[0])],
    }),
    [ringBands],
  )

  const agentBoard = useMemo(
    () => sumBoardBy(rows, 'agentName', 'callsOffered', { emptyLabel: 'Unassigned' }),
    [rows],
  )
  const campaignBoard = useMemo(
    () => sumBoardBy(rows, 'campaignName', 'callsOffered', { emptyLabel: 'No campaign' }),
    [rows],
  )
  const talkBoard = useMemo(
    () => sumBoardBy(rows, 'agentName', 'totalTalkSec', { emptyLabel: 'Unassigned' }),
    [rows],
  )

  if (rows.length === 0 || totals.offered === 0) {
    return (
      <AnalyticsCanvas>
        <div className="rounded-[4px] border border-slate-200 bg-white px-6 py-16 text-center">
          <p className="text-sm font-semibold text-slate-900">Nothing to analyse</p>
          <p className="mt-1 text-xs text-slate-500">
            No outbound call was placed in this window. Widen the range, or clear the campaign and
            agent filters, then run the report again.
          </p>
        </div>
      </AnalyticsCanvas>
    )
  }

  const connectRate = ratePct(totals.answered, totals.offered)
  const abandonRate = ratePct(totals.abandoned, totals.offered)
  const abandonTone = (abandonRate ?? 0) > ABANDON_CAP_PCT ? 'critical' : 'good'

  return (
    <AnalyticsCanvas>
      <HeroBand
        eyebrow="Outbound attempts"
        value={formatCount(totals.offered)}
        caption={`Across ${formatCount(rows.length)} interval${rows.length === 1 ? '' : 's'} in the selected window`}
        spark={offered.values}
        Icon={PhoneForwarded}
        facts={[
          { label: 'Connect rate', value: formatPct(connectRate, 1), tone: (connectRate ?? 0) >= 40 ? 'good' : 'warning' },
          { label: 'Abandon rate', value: formatPct(abandonRate, 1), tone: abandonTone },
          { label: 'Average handle time', value: formatSeconds(avgHandle) },
          { label: 'Total talk time', value: formatSeconds(totals.talk) },
        ]}
      />

      <KpiRow>
        <StatTile label="Connected" value={formatCount(totals.answered)} hint={formatPct(connectRate, 1)} />
        <StatTile label="Not connected" value={formatCount(totals.unanswered)} />
        <StatTile
          label="Dialler abandons"
          value={formatCount(totals.abandoned)}
          tone={abandonTone}
          hint={`${formatPct(abandonRate, 1)} — cap is ${ABANDON_CAP_PCT}%`}
        />
        <StatTile label="Busy" value={formatCount(totals.busy)} />
        <StatTile label="No answer" value={formatCount(totals.noAnswer)} />
        <StatTile
          label="Rejected or failed"
          value={formatCount(totals.rejected)}
          tone={totals.rejected > 0 ? 'warning' : 'default'}
        />
      </KpiRow>

      {/* ── Volume ── */}
      <section className="flex flex-col gap-3">
        <SectionLabel note={`${rows.length} interval${rows.length === 1 ? '' : 's'}`}>
          Attempts through the window
        </SectionLabel>
        <CardGrid>
          <ChartCard
            title="Attempted and connected"
            subtitle="On the interval chosen in the filters"
            Icon={CalendarClock}
            wide
            height={300}
            footnote={
              busiest
                ? `Busiest interval: ${bucketLabel(busiest.label)} with ${formatCount(busiest.value)} attempts.`
                : undefined
            }
          >
            {offered.labels.length === 0 ? (
              <CardEmpty message="No interval in this result set." />
            ) : (
              <Chart
                type="line"
                data={volumeData}
                options={lineOptions({ tickLabel: bucketLabel })}
                className="h-full w-full"
              />
            )}
          </ChartCard>

          <ChartCard
            title="Connect rate against abandon rate"
            subtitle="The pacing trade-off, per interval"
            Icon={TrendingUp}
            wide
            height={300}
            footnote={`Across the window: ${formatPct(connectRate, 1)} connected, ${formatPct(abandonRate, 1)} abandoned by the dialler.`}
          >
            {offered.labels.length === 0 ? (
              <CardEmpty message="No interval in this result set." />
            ) : (
              <Chart
                type="line"
                data={rateData}
                options={lineOptions({ tickLabel: bucketLabel })}
                className="h-full w-full"
              />
            )}
          </ChartCard>
        </CardGrid>
      </section>

      {/* ── Outcomes ── */}
      <section className="flex flex-col gap-3">
        <SectionLabel>What happened to the attempts</SectionLabel>
        <CardGrid>
          <ChartCard title="Outcome" subtitle="Every attempt, once" height={220} Icon={PhoneOff}>
            <Meter
              segments={[
                { label: 'Connected', value: totals.answered, color: VIZ.series[0] },
                { label: 'Busy', value: totals.busy, color: VIZ.series[1] },
                { label: 'No answer', value: totals.noAnswer, color: VIZ.series[2] },
                { label: 'Rejected', value: totals.rejected, color: VIZ.status.critical },
              ]}
              emptyMessage="No outcome recorded on these attempts."
            />
          </ChartCard>

          <ChartCard
            title="Time to connect"
            subtitle="Connected calls, by seconds of ringing"
            Icon={Timer}
            wide
            height={280}
            footnote="Long rings before an answer are the attempts a customer nearly did not take."
          >
            <Chart type="bar" data={bandData} options={barOptions()} className="h-full w-full" />
          </ChartCard>

          <ChartCard title="Averages" subtitle="Weighted by call volume, not by interval" Icon={Timer}>
            <BarList
              items={[
                { label: 'Handle time', value: Math.round(avgHandle ?? 0), meta: formatSeconds(avgHandle) },
                { label: 'Talk time', value: Math.round(avgTalk ?? 0), meta: formatSeconds(avgTalk) },
                { label: 'Ring time', value: Math.round(avgRing ?? 0), meta: formatSeconds(avgRing) },
              ]}
              format={(v) => formatSeconds(v)}
              emptyMessage="No durations recorded."
            />
          </ChartCard>
        </CardGrid>
      </section>

      {/* ── Who ── */}
      <section className="flex flex-col gap-3">
        <SectionLabel>Who is dialling</SectionLabel>
        <CardGrid>
          <ChartCard title="Attempts by agent" subtitle="Calls placed" Icon={Users}>
            <BarList
              items={agentBoard.map((e) => ({
                label: e.label,
                value: e.value,
                meta: formatPct(ratePct(e.value, totals.offered), 1),
              }))}
              format={formatCount}
              emptyMessage="No agent recorded on these intervals."
            />
          </ChartCard>
          <ChartCard title="Talk time by agent" subtitle="Total seconds on connected calls" Icon={Timer}>
            <BarList
              items={talkBoard.map((e) => ({
                label: e.label,
                value: Math.round(e.value),
                meta: formatSeconds(e.value),
              }))}
              format={(v) => formatSeconds(v)}
              emptyMessage="No talk time recorded."
            />
          </ChartCard>
          <ChartCard title="By campaign" subtitle="Calls placed" Icon={Layers}>
            <BarList
              items={campaignBoard.map((e) => ({
                label: e.label,
                value: e.value,
                meta: formatPct(ratePct(e.value, totals.offered), 1),
              }))}
              format={formatCount}
              emptyMessage="No campaign recorded on these intervals."
            />
          </ChartCard>
        </CardGrid>
      </section>
    </AnalyticsCanvas>
  )
}
